import React from 'react'
import { Link } from 'react-router-dom'
import { Lock, ShieldCheck, ClipboardList, KeyRound, FileCheck2, Settings2 } from 'lucide-react'

export default function Security() {
  return (
    <section className="section light" style={{ minHeight: '70vh' }}>
      <h2>Security &amp; Privacy</h2>
      <p className="lead muted">
        A description of what's actually implemented today — not a marketing claim of full compliance.
        Where something is in progress rather than complete, we say so.
      </p>

      <div className="security-grid">
        <div className="security-item">
          <KeyRound size={20} />
          <div>
            <h4>Multi-factor authentication</h4>
            <p>Required for privileged administrative accounts (platform and payer administrators), enforced before any other action.</p>
          </div>
        </div>
        <div className="security-item">
          <ShieldCheck size={20} />
          <div>
            <h4>Role-based access control</h4>
            <p>Every user role is scoped to the organization, provider, or patient data it's authorized to see — reviewed endpoint by endpoint.</p>
          </div>
        </div>
        <div className="security-item">
          <ClipboardList size={20} />
          <div>
            <h4>Audit logs</h4>
            <p>Security-relevant actions — logins, administrative changes, MFA events — are recorded with who did what and when.</p>
          </div>
        </div>
        <div className="security-item">
          <Lock size={20} />
          <div>
            <h4>Encryption</h4>
            <p>Data is encrypted in transit (TLS) between users and the platform. Sensitive credentials, such as multi-factor authentication secrets, are additionally encrypted at rest.</p>
          </div>
        </div>
        <div className="security-item">
          <FileCheck2 size={20} />
          <div>
            <h4>Consent management</h4>
            <p>Terms of Service and Privacy Policy acceptance is recorded per account, versioned to the policy in effect at the time.</p>
          </div>
        </div>
        <div className="security-item">
          <Settings2 size={20} />
          <div>
            <h4>Administrative controls</h4>
            <p>Sensitive actions — creating administrative users, forcing a session logout, disabling multi-factor authentication — require a fresh authentication step, not just a standing login.</p>
          </div>
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Healthcare Compliance Posture</h2></div>
        <p className="lead muted">
          ATUUNE is built to support outpatient behavioral health workflows. Our hosting infrastructure
          (database and application hosting) supports upgrading to a Business Associate Agreement
          ("BAA")-eligible tier — <strong>this upgrade has not yet been enabled</strong>. Where a BAA is
          required for a given customer relationship, it can be executed separately, contingent on that
          infrastructure upgrade being completed first. Providers and organizations using ATUUNE remain
          responsible for their own compliance with applicable healthcare laws and professional obligations.
        </p>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Integrations</h2></div>
        <p className="lead muted">ATUUNE is designed to work alongside the systems you already use — not replace them.</p>
        <div className="cards">
          <div className="card">
            <h3>Calendar sync<span className="status-tag live">Available today</span></h3>
            <p>Syncs with the calendar you already use via a secure iCal (ICS) feed — compatible with Google Calendar, Outlook, and Apple Calendar through their standard calendar-sharing export.</p>
          </div>
          <div className="card">
            <h3>EHR<span className="status-tag roadmap">Roadmap</span></h3>
            <p>Direct EHR integration is on our roadmap and not yet available.</p>
          </div>
          <div className="card">
            <h3>Practice Management<span className="status-tag roadmap">Roadmap</span></h3>
            <p>Direct practice management system integration is on our roadmap and not yet available.</p>
          </div>
          <div className="card">
            <h3>Claims<span className="status-tag roadmap">Roadmap</span></h3>
            <p>Claims data integration is on our roadmap and not yet available.</p>
          </div>
          <div className="card">
            <h3>Provider Directories<span className="status-tag roadmap">Roadmap</span></h3>
            <p>Direct provider directory integration is on our roadmap and not yet available.</p>
          </div>
        </div>
      </div>

      <div className="actions" style={{ marginTop: 28 }}>
        <Link to="/contact">Request Demo</Link>
        <Link className="secondary outline-dark" to="/resources">Read the FAQ</Link>
      </div>
    </section>
  )
}
